export function createLubeHubApi(client) {
    return {
        lotosStorefront: {
            products: (params, options) => client.getPaginated('/agency/storefront/products', {
                ...options,
                query: { ...params },
            }),
            product: (id, options) => client.get(`/agency/storefront/products/${String(id)}`, options),
            orders: (params, options) => client.getPaginated('/agency/storefront/orders', {
                ...options,
                query: { ...params },
            }),
            order: (id, options) => client.get(`/agency/storefront/orders/${String(id)}`, options),
            placePersonalOrder: (input, options) => client.post('/agency/storefront/orders', input, options),
            placeStoreOrder: (input, options) => client.post('/agency/storefront/store-orders', input, options),
        },
        runtimeConfig: (params, options) => client.get('/app-config', {
            ...options,
            query: { ...params },
        }),
        auth: {
            register: (input, options) => client.post('/auth/register', input, options),
            login: (input, options) => client.post('/auth/login', input, options),
            me: (options) => client.get('/auth/me', options),
            governorates: (options) => client.get('/governorates', options),
            updateDiscoveryGovernorate: (code, options) => client.put('/auth/me/discovery-governorate', { governorate: code }, options),
            updateProfile: (input, options) => client.patch('/auth/me', input, options),
            logout: async (options) => {
                await client.post('/auth/logout', {}, options);
            },
            forgotPassword: (input, options) => client.post('/auth/forgot-password', input, options),
            resetPassword: (input, options) => client.post('/auth/reset-password', input, options),
            verifyEmail: (input, options) => client.post('/auth/email/verify', input, options),
            resendVerification: (options) => client.post('/auth/email/verification-notification', {}, options),
            listingLimits: (options) => client.get('/auth/me/listing-limits', options),
            deletionSummary: (options) => client.get('/auth/me/deletion-summary', options),
            acceptLegalConsent: (input, options) => client.post('/auth/me/legal-consents', input, options),
            deleteAccount: (input, options) => client.post('/auth/me/account-deletion', input, options),
        },
        legal: {
            current: (options) => client.get('/legal/current', options),
        },
        vehicles: {
            list: (params, options) => client.getPaginated('/vehicles', { ...options, query: { ...params } }),
            get: (id, options) => client.get(`/vehicles/${String(id)}`, options),
            create: (input, options) => client.post('/vehicles', input, options),
            update: (id, input, options) => client.patch(`/vehicles/${String(id)}`, input, options),
            remove: (id, options) => client.delete(`/vehicles/${String(id)}`, undefined, options),
            workshopAccess: {
                get: (vehicleId, options) => client.get(`/vehicles/${String(vehicleId)}/workshop-access`, options),
                issue: (vehicleId, input, options) => client.post(`/vehicles/${String(vehicleId)}/workshop-access`, input ?? {}, options),
                revoke: (vehicleId, options) => client.delete(`/vehicles/${String(vehicleId)}/workshop-access`, undefined, options),
            },
            transfer: {
                get: (vehicleId, options) => client.get(`/vehicles/${String(vehicleId)}/transfer`, options),
                create: (vehicleId, options) => client.post(`/vehicles/${String(vehicleId)}/transfer`, {}, options),
                withdraw: (vehicleId, options) => client.delete(`/vehicles/${String(vehicleId)}/transfer`, undefined, options),
            },
            serviceRecords: {
                list: (vehicleId, params, options) => client.getPaginated(`/vehicles/${String(vehicleId)}/service-records`, {
                    ...options,
                    query: { ...params },
                }),
                get: (vehicleId, recordId, options) => client.get(`/vehicles/${String(vehicleId)}/service-records/${String(recordId)}`, options),
                create: (vehicleId, input, options) => client.post(`/vehicles/${String(vehicleId)}/service-records`, input, options),
                remove: (vehicleId, recordId, options) => client.delete(`/vehicles/${String(vehicleId)}/service-records/${String(recordId)}`, undefined, options),
            },
            invoices: {
                get: (vehicleId, invoiceId, options) => client.get(`/vehicles/${String(vehicleId)}/invoices/${String(invoiceId)}`, options),
            },
        },
        vehicleTransfers: {
            resolve: (input, options) => client.post('/vehicle-transfers/resolve', input, options),
            accept: (input, options) => client.post('/vehicle-transfers/accept', input, options),
        },
        marketplace: {
            browse: (params, options) => client.getPaginated('/vehicle-listings', {
                ...options,
                query: { ...params },
            }),
            get: (id, options) => client.get(`/vehicle-listings/${String(id)}`, options),
            listVehicle: (vehicleId, input, options) => client.post(`/vehicles/${String(vehicleId)}/listing`, input, options),
            mine: {
                list: (params, options) => client.getPaginated('/vehicle-listings/mine', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/vehicle-listings/mine/${String(id)}`, options),
                update: (id, input, options) => client.patch(`/vehicle-listings/mine/${String(id)}`, input, options),
                setPhotos: (id, input, options) => client.put(`/vehicle-listings/mine/${String(id)}/media`, input, options),
                publish: (id, options) => client.post(`/vehicle-listings/mine/${String(id)}/publication`, undefined, options),
                pause: (id, options) => client.delete(`/vehicle-listings/mine/${String(id)}/publication`, undefined, options),
                withdraw: (id, options) => client.post(`/vehicle-listings/mine/${String(id)}/withdrawal`, undefined, options),
                markSold: (id, options) => client.post(`/vehicle-listings/mine/${String(id)}/sale`, undefined, options),
                inquiries: (id, params, options) => client.getPaginated(`/vehicle-listings/mine/${String(id)}/inquiries`, { ...options, query: { ...params } }),
            },
            favorites: {
                list: (params, options) => client.getPaginated('/vehicle-listings/favorites', {
                    ...options,
                    query: { ...params },
                }),
                // Awaited and discarded rather than typed `put<void>`: the endpoint
                // answers 204 with no body, and `void` as a type argument is not a
                // thing the linter will accept.
                add: async (id, options) => {
                    await client.put(`/vehicle-listings/${String(id)}/favorite`, undefined, options);
                },
                remove: (id, options) => client.delete(`/vehicle-listings/${String(id)}/favorite`, undefined, options),
            },
            enquire: (id, input, options) => client.post(`/vehicle-listings/${String(id)}/inquiries`, input ?? {}, options),
        },
        vehicleCatalogue: {
            groups: (params, options) => client.getPaginated('/vehicle-catalogue/groups', {
                ...options,
                query: { ...params },
            }),
            makes: (params, options) => client.getPaginated('/vehicle-catalogue/makes', {
                ...options,
                query: { ...params },
            }),
            models: (params, options) => client.getPaginated('/vehicle-catalogue/models', {
                ...options,
                query: { ...params },
            }),
            options: (options) => client.get('/vehicle-catalogue/options', options),
        },
        media: {
            upload: (image, options) => {
                const form = new FormData();
                // `file` is the field the API validates. The cast covers React Native,
                // whose `FormData` accepts a `{ uri, name, type }` descriptor that the
                // DOM type does not describe — there is no `File` on a phone, and
                // reading the image into memory to build one would load several
                // megabytes to no purpose.
                form.append('file', image);
                return client.post('/media', form, options);
            },
            uploadDocument: (document, options) => {
                const form = new FormData();
                form.append('file', document);
                return client.post('/media/documents', form, options);
            },
            remove: (id, options) => client.delete(`/media/${String(id)}`, undefined, options),
        },
        admin: {
            miniBusinesses: {
                list: (params, options) => client.get('/admin/mini-businesses', { ...options, query: params }),
                get: (type, id, params, options) => client.get(`/admin/mini-businesses/${type}/${String(id)}`, {
                    ...options,
                    query: params,
                }),
            },
            dashboard: {
                get: (period = '7d', options) => client.get('/admin/dashboard', { ...options, query: { period } }),
            },
            vehicleListings: {
                list: (params, options) => client.getPaginated('/admin/vehicle-listings', {
                    ...options,
                    query: { ...params },
                }),
                summary: (options) => client.get('/admin/vehicle-listings/summary', options),
                get: (id, options) => client.get(`/admin/vehicle-listings/${String(id)}`, options),
                create: (input, options) => client.post('/admin/vehicle-listings', input, options),
                update: (id, input, options) => client.patch(`/admin/vehicle-listings/${String(id)}`, input, options),
                approve: (id, reason, options) => client.post(`/admin/vehicle-listings/${String(id)}/approval`, reason === undefined ? {} : { reason }, options),
                reject: (id, reason, options) => client.post(`/admin/vehicle-listings/${String(id)}/rejection`, { reason }, options),
                suspend: (id, reason, options) => client.post(`/admin/vehicle-listings/${String(id)}/suspension`, { reason }, options),
                markSold: (id, reason, options) => client.post(`/admin/vehicle-listings/${String(id)}/sale`, reason === undefined ? {} : { reason }, options),
                archive: async (id, reason, options) => {
                    await client.delete(`/admin/vehicle-listings/${String(id)}`, reason === undefined ? undefined : { reason }, options);
                },
                inquiries: (id, params, options) => client.getPaginated(`/admin/vehicle-listings/${String(id)}/inquiries`, { ...options, query: { ...params } }),
            },
            search: (query, options) => client.get('/admin/search', { ...options, query: { query } }),
            users: {
                list: (params, options) => client.getPaginated('/admin/users', { ...options, query: { ...params } }),
                get: (id, options) => client.get(`/admin/users/${String(id)}`, options),
                update: (id, input, options) => client.patch(`/admin/users/${String(id)}`, input, options),
                disable: (id, reason, options) => client.post(`/admin/users/${String(id)}/disable`, { reason }, options),
                enable: (id, options) => client.post(`/admin/users/${String(id)}/enable`, {}, options),
                grantAdministrator: (id, options) => client.post(`/admin/users/${String(id)}/grant-administrator`, {}, options),
                revokeAdministrator: (id, reason, options) => client.post(`/admin/users/${String(id)}/revoke-administrator`, { reason }, options),
                setPassword: (id, input, options) => client.post(`/admin/users/${String(id)}/password`, input, options),
                logoutAll: (id, reason, options) => client.post(`/admin/users/${String(id)}/logout-all`, reason === undefined ? {} : { reason }, options),
                uploadAvatar: (id, image, options) => {
                    const form = new FormData();
                    form.append('file', image);
                    return client.post(`/admin/users/${String(id)}/avatar`, form, options);
                },
                removeAvatar: (id, reason, options) => client.delete(`/admin/users/${String(id)}/avatar`, reason === undefined ? undefined : { reason }, options),
                deletionImpact: (id, options) => client.get(`/admin/users/${String(id)}/deletion-impact`, options),
                remove: (id, input, options) => client.delete(`/admin/users/${String(id)}`, input, options),
                listingLimits: {
                    get: (id, options) => client.get(`/admin/users/${String(id)}/listing-limits`, options),
                    update: (id, input, options) => client.put(`/admin/users/${String(id)}/listing-limits`, { ...input }, options),
                },
            },
            warehouses: {
                list: (params, options) => client.getPaginated('/admin/warehouses', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/warehouses/${String(id)}`, options),
                create: (input, options) => client.post('/admin/warehouses', input, options),
                update: (id, input, options) => client.patch(`/admin/warehouses/${String(id)}`, input, options),
                // The reason travels in the body of a DELETE, the way every other
                // administrative removal on this API carries one.
                archive: (id, reason, options) => client.delete(`/admin/warehouses/${String(id)}`, reason === undefined ? undefined : { reason }, options),
                restore: (id, options) => client.post(`/admin/warehouses/${String(id)}/restore`, {}, options),
                members: {
                    add: (id, input, options) => client.post(`/admin/warehouses/${String(id)}/members`, input, options),
                    remove: (id, memberId, reason, options) => client.delete(`/admin/warehouses/${String(id)}/members/${String(memberId)}`, reason === undefined ? undefined : { reason }, options),
                },
                receipts: (id, params, options) => client.getPaginated(`/admin/warehouses/${String(id)}/receipts`, {
                    ...options,
                    query: { ...params },
                }),
                inventory: (id, params, options) => client.getPaginated(`/admin/warehouses/${String(id)}/inventory`, { ...options, query: { ...params } }),
            },
            vehicles: {
                list: (params, options) => client.getPaginated('/admin/vehicles', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/vehicles/${String(id)}`, options),
                update: (id, input, options) => client.patch(`/admin/vehicles/${String(id)}`, input, options),
                setOdometer: (id, input, options) => client.post(`/admin/vehicles/${String(id)}/odometer`, input, options),
                changeOwner: (id, input, options) => client.post(`/admin/vehicles/${String(id)}/owner`, input, options),
                archive: (id, input, options) => client.post(`/admin/vehicles/${String(id)}/archive`, input, options),
                restore: (id, reason, options) => client.post(`/admin/vehicles/${String(id)}/restore`, reason === undefined ? {} : { reason }, options),
                deletionImpact: (id, options) => client.get(`/admin/vehicles/${String(id)}/deletion-impact`, options),
                remove: (id, input, options) => client.delete(`/admin/vehicles/${String(id)}`, input, options),
                uploadPhoto: (id, image, options) => {
                    const form = new FormData();
                    form.append('file', image);
                    return client.post(`/admin/vehicles/${String(id)}/photo`, form, options);
                },
                removePhoto: (id, reason, options) => client.delete(`/admin/vehicles/${String(id)}/photo`, reason === undefined ? undefined : { reason }, options),
                cancelTransfer: (id, transferId, reason, options) => client.post(`/admin/vehicles/${String(id)}/transfers/${String(transferId)}/cancel`, { reason }, options),
                revokeQr: (id, grantId, reason, options) => client.post(`/admin/vehicles/${String(id)}/qr/${String(grantId)}/revoke`, { reason }, options),
                regenerateQr: (id, reason, options) => client.post(`/admin/vehicles/${String(id)}/qr/regenerate`, { reason }, options),
                seedMaintenance: (id, reason, options) => client.post(`/admin/vehicles/${String(id)}/maintenance/seed`, reason === undefined ? {} : { reason }, options),
                updateMaintenance: (id, type, input, options) => client.patch(`/admin/vehicles/${String(id)}/maintenance/${type}`, input, options),
                createServiceRecord: (id, input, options) => client.post(`/admin/vehicles/${String(id)}/service-records`, input, options),
                updateServiceRecord: (id, recordId, input, options) => client.patch(`/admin/vehicles/${String(id)}/service-records/${String(recordId)}`, input, options),
                removeServiceRecord: (id, recordId, reason, options) => client.delete(`/admin/vehicles/${String(id)}/service-records/${String(recordId)}`, { reason }, options),
            },
            workshops: listingSection(client, '/admin/workshops'),
            stores: listingSection(client, '/admin/stores'),
            products: {
                list: (params, options) => client.getPaginated('/admin/products', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/products/${String(id)}`, options),
                archive: (id, reason, options) => client.post(`/admin/products/${String(id)}/archive`, { reason }, options),
                restore: (id, options) => client.post(`/admin/products/${String(id)}/restore`, {}, options),
            },
            agencyOrders: {
                list: (params, options) => client.getPaginated('/admin/agency/orders', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/agency/orders/${String(id)}`, options),
                setStatus: (id, input, options) => client.post(`/admin/agency/orders/${String(id)}/status`, input, options),
            },
            agencyProducts: {
                list: (params, options) => client.getPaginated('/admin/agency/products', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/agency/products/${String(id)}`, options),
                create: (input, options) => client.post('/admin/agency/products', input, options),
                update: (id, input, options) => client.patch(`/admin/agency/products/${String(id)}`, input, options),
            },
            masterProducts: {
                list: (params, options) => client.getPaginated('/admin/master-products', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/master-products/${String(id)}`, options),
                create: (input, options) => client.post('/admin/master-products', input, options),
                update: (id, input, options) => client.patch(`/admin/master-products/${String(id)}`, input, options),
                attachMedia: (id, input, options) => client.post(`/admin/master-products/${String(id)}/media`, input, options),
                updateMedia: (id, attachmentId, input, options) => client.patch(`/admin/master-products/${String(id)}/media/${String(attachmentId)}`, input, options),
                makeMediaPrimary: (id, attachmentId, options) => client.post(`/admin/master-products/${String(id)}/media/${String(attachmentId)}/primary`, undefined, options),
                detachMedia: (id, attachmentId, options) => client.delete(`/admin/master-products/${String(id)}/media/${String(attachmentId)}`, undefined, options),
            },
            agencies: {
                list: (params, options) => client.get('/admin/agencies', { ...options, query: { ...params } }),
                get: (id, options) => client.get(`/admin/agencies/${String(id)}`, options),
                create: (input, options) => client.post('/admin/agencies', input, options),
                update: (id, input, options) => client.patch(`/admin/agencies/${String(id)}`, input, options),
                warehouses: (id, options) => client.get(`/admin/agencies/${String(id)}/warehouses`, options),
                addWarehouse: (id, input, options) => client.post(`/admin/agencies/${String(id)}/warehouses`, input, options),
                updateWarehouse: (id, warehouseId, input, options) => client.patch(`/admin/agencies/${String(id)}/warehouses/${String(warehouseId)}`, input, options),
                addMember: (id, userId, input, options) => client.post(`/admin/agencies/${String(id)}/members`, { user_id: userId, ...input }, options),
                updateMember: (id, userId, input, options) => client.patch(`/admin/agencies/${String(id)}/members/${String(userId)}`, input, options),
                removeMember: (id, userId, options) => client.delete(`/admin/agencies/${String(id)}/members/${String(userId)}`, undefined, options),
            },
            agencyWarehouse: {
                dashboard: (params, options) => client.get('/admin/agency/dashboard', {
                    ...options,
                    query: { ...params },
                }),
                stock: (params, options) => client.getPaginated('/admin/agency/stock', {
                    ...options,
                    query: { ...params },
                }),
                productStock: (productId, params, options) => client.get(`/admin/agency/products/${String(productId)}/stock`, { ...options, query: { ...params } }),
                reservations: (params, options) => client.getPaginated('/admin/agency/reservations', {
                    ...options,
                    query: { ...params },
                }),
                awaitingPricing: (params, options) => client.getPaginated('/admin/agency/awaiting-pricing', {
                    ...options,
                    query: { ...params },
                }),
                receipts: {
                    list: (params, options) => client.getPaginated('/admin/agency/receipts', {
                        ...options,
                        query: { ...params },
                    }),
                    get: (id, options) => client.get(`/admin/agency/receipts/${String(id)}`, options),
                },
                recordCosts: (input, options) => client.post('/admin/agency/costs', input, options),
                costLayers: (productId, params, options) => client.get(`/admin/agency/products/${String(productId)}/cost-layers`, {
                    ...options,
                    query: { ...params },
                }),
                suppliers: {
                    list: (params, options) => client.getPaginated('/admin/agency/suppliers', {
                        ...options,
                        query: { ...params },
                    }),
                    get: (id, options) => client.get(`/admin/agency/suppliers/${String(id)}`, options),
                    create: (input, options) => client.post('/admin/agency/suppliers', input, options),
                    update: (id, input, options) => client.patch(`/admin/agency/suppliers/${String(id)}`, input, options),
                    remove: (id, options) => client.delete(`/admin/agency/suppliers/${String(id)}`, undefined, options),
                },
                invoices: {
                    list: (params, options) => client.getPaginated('/admin/agency/supplier-invoices', {
                        ...options,
                        query: { ...params },
                    }),
                    get: (id, options) => client.get(`/admin/agency/supplier-invoices/${String(id)}`, options),
                    record: (input, options) => client.post('/admin/agency/supplier-invoices', input, options),
                    linkReceipts: (id, receiptIds, options) => client.post(`/admin/agency/supplier-invoices/${String(id)}/receipts`, { receipt_ids: receiptIds }, options),
                },
                pricing: {
                    get: (productId, options) => client.get(`/admin/agency/products/${String(productId)}/pricing`, options),
                    set: (productId, prices, options) => client.put(`/admin/agency/products/${String(productId)}/pricing`, { prices }, options),
                    publish: (productId, options) => client.post(`/admin/agency/products/${String(productId)}/publish`, {}, options),
                    unpublish: (productId, options) => client.post(`/admin/agency/products/${String(productId)}/unpublish`, {}, options),
                },
                priceTiers: {
                    list: (params, options) => client.getPaginated('/admin/agency/price-tiers', {
                        ...options,
                        query: { ...params },
                    }),
                    grant: (input, options) => client.post('/admin/agency/price-tiers', input, options),
                    revoke: (id, options) => client.delete(`/admin/agency/price-tiers/${String(id)}`, undefined, options),
                },
            },
            serviceRecords: {
                list: (params, options) => client.getPaginated('/admin/service-records', {
                    ...options,
                    query: { ...params },
                }),
            },
            wallets: {
                list: (params, options) => client.getPaginated('/admin/wallets', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/wallets/${String(id)}`, options),
            },
            walletTransactions: {
                list: (params, options) => client.getPaginated('/admin/wallet-transactions', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/wallet-transactions/${String(id)}`, options),
            },
            walletAccounts: {
                list: (params, options) => client.getPaginated('/admin/wallet-accounts', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/wallet-accounts/${String(id)}`, options),
            },
            systemWallet: {
                adjust: (input, options) => client.post('/admin/wallet/system/adjust', input, options),
                credit: (input, options) => client.post('/admin/wallet/system/credit', input, options),
            },
            walletTransfers: {
                list: (params, options) => client.getPaginated('/admin/wallet-transfers', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/wallet-transfers/${String(id)}`, options),
            },
            rewards: {
                list: (params, options) => client.getPaginated('/admin/rewards', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/rewards/${String(id)}`, options),
            },
            notifications: {
                list: (params, options) => client.getPaginated('/admin/notifications', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/notifications/${String(id)}`, options),
                devices: (userId, params, options) => client.getPaginated(`/admin/users/${String(userId)}/push-devices`, {
                    ...options,
                    query: { ...params },
                }),
                preview: (userId, input, options) => client.post(`/admin/users/${String(userId)}/notifications/preview`, input, options),
                send: (userId, input, options) => client.post(`/admin/users/${String(userId)}/notifications`, input, options),
            },
            settings: {
                get: (options) => client.get('/admin/settings', options),
                update: (settings, options) => client.patch('/admin/settings', { settings }, options),
            },
            runtimeConfig: {
                get: (options) => client.get('/admin/runtime-config', options),
                update: (input, options) => client.patch('/admin/runtime-config', input, options),
                preview: (surface, audience, options) => client.post('/admin/runtime-config/preview', { surface, audience }, options),
            },
            auditLogs: {
                list: (params, options) => client.getPaginated('/admin/audit-logs', {
                    ...options,
                    query: { ...params },
                }),
            },
            platformRoles: {
                list: (options) => client.get('/admin/platform-roles', options),
            },
            platformPermissions: {
                list: (options) => client.get('/admin/platform-permissions', options),
            },
            businessClaims: {
                list: (params, options) => client.getPaginated('/admin/business-claims', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/admin/business-claims/${String(id)}`, options),
                startReview: (id, options) => client.post(`/admin/business-claims/${String(id)}/review`, {}, options),
                recordPhoneCall: (id, input, options) => client.post(`/admin/business-claims/${String(id)}/phone-verification`, input, options),
                requestInformation: (id, input, options) => client.post(`/admin/business-claims/${String(id)}/information-request`, input, options),
                approve: (id, input, options) => client.post(`/admin/business-claims/${String(id)}/approval`, input ?? {}, options),
                reject: (id, input, options) => client.post(`/admin/business-claims/${String(id)}/rejection`, input, options),
            },
            businesses: {
                updateLocation: (type, id, input, options) => client.patch(`/admin/businesses/${type}/${String(id)}/location`, input, options),
                updateCategories: (type, id, input, options) => client.put(`/admin/businesses/${type}/${String(id)}/categories`, input, options),
                updateVerification: (type, id, input, options) => client.patch(`/admin/businesses/${type}/${String(id)}/verification`, input, options),
            },
            platformAccess: {
                get: (userId, options) => client.get(`/admin/users/${String(userId)}/platform-access`, options),
                updateRoles: (userId, input, options) => client.put(`/admin/users/${String(userId)}/platform-roles`, input, options),
            },
        },
        workshops: {
            list: (params, options) => client.getPaginated('/workshops', { ...options, query: { ...params } }),
            get: (id, options) => client.get(`/workshops/${String(id)}`, options),
        },
        stores: {
            list: (params, options) => client.getPaginated('/stores', { ...options, query: { ...params } }),
            get: (id, options) => client.get(`/stores/${String(id)}`, options),
        },
        products: {
            list: (params, options) => client.getPaginated('/products', {
                ...options,
                query: { ...params },
            }),
            get: (id, options) => client.get(`/products/${String(id)}`, options),
        },
        serviceCatalogue: {
            list: (options) => client.get('/service-catalogue', options),
        },
        businessCategories: {
            list: (options) => client.get('/business-categories', options),
        },
        businessClaims: {
            submit: (type, businessId, input, options) => client.post(`/businesses/${type}/${String(businessId)}/claims`, input, options),
            list: (params, options) => client.getPaginated('/auth/me/business-claims', {
                ...options,
                query: { ...params },
            }),
            get: (id, options) => client.get(`/auth/me/business-claims/${String(id)}`, options),
            cancel: (id, options) => client.post(`/auth/me/business-claims/${String(id)}/cancellation`, {}, options),
            addEvidence: (id, mediaIds, options) => client.post(`/auth/me/business-claims/${String(id)}/evidence`, { media_ids: mediaIds }, options),
        },
        myWorkshop: {
            list: (options) => client.get('/workshops/owned', options),
            lifecycle: {
                pause: (id, options) => client.post(`/workshops/${String(id)}/pause`, {}, options),
                resume: (id, options) => client.post(`/workshops/${String(id)}/resume`, {}, options),
                archiveImpact: (id, options) => client.get(`/workshops/${String(id)}/archive-impact`, options),
                // Addressed by id, never "mine": an account may own several, and a
                // destructive call that resolved "the caller's" would archive the
                // oldest one whatever the owner meant.
                archive: (id, reason, options) => client.delete(`/workshops/${String(id)}`, reason === undefined ? undefined : { reason }, options),
            },
            get: (options) => client.get('/workshops/mine', options),
            apply: (input, options) => client.post('/workshops', input, options),
            update: (input, options) => client.patch('/workshops/mine', input, options),
            services: {
                list: (options) => client.get('/workshops/mine/services', options),
                replace: (slugs, options) => client.put('/workshops/mine/services', { services: slugs }, options),
            },
            intake: {
                claim: (code, options) => client.post('/workshops/mine/intake', { code }, options),
                get: (grantId, options) => client.get(`/workshops/mine/intake/${String(grantId)}`, options),
            },
            visits: {
                list: (params, options) => client.getPaginated('/workshops/mine/service-records', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/workshops/mine/service-records/${String(id)}`, options),
            },
            invoices: {
                issue: (recordId, options) => client.post(`/workshops/mine/service-records/${String(recordId)}/invoice`, {}, options),
                get: (id, options) => client.get(`/workshops/mine/invoices/${String(id)}`, options),
                void: (id, input, options) => client.post(`/workshops/mine/invoices/${String(id)}/void`, input, options),
            },
            customers: {
                list: (params, options) => client.getPaginated('/workshops/mine/customers', {
                    ...options,
                    query: { ...params },
                }),
                invite: (input, options) => client.post('/workshops/mine/customers', input, options).then(() => undefined),
                remove: (id, options) => client.delete(`/workshops/mine/customers/${String(id)}`, undefined, options),
                vehicles: (id, params, options) => client.getPaginated(`/workshops/mine/customers/${String(id)}/vehicles`, {
                    ...options,
                    query: { ...params },
                }),
            },
            agency: {
                products: (options) => client.get('/agency/products', options),
                workshops: (options) => client.get('/agency/workshops', options),
                orders: (workshopId, params, options) => client.getPaginated(`/agency/workshops/${String(workshopId)}/orders`, {
                    ...options,
                    query: { ...params },
                }),
                order: (workshopId, orderId, options) => client.get(`/agency/workshops/${String(workshopId)}/orders/${String(orderId)}`, options),
                place: (workshopId, input, options) => client.post(`/agency/workshops/${String(workshopId)}/orders`, input, options),
                cancel: (workshopId, orderId, options) => client.post(`/agency/workshops/${String(workshopId)}/orders/${String(orderId)}/cancel`, {}, options),
            },
        },
        warehouses: {
            list: (params, options) => client.getPaginated('/warehouses', { ...options, query: { ...params } }),
            get: (id, options) => client.get(`/warehouses/${String(id)}`, options),
            archived: (params, options) => client.getPaginated('/warehouses/archived', {
                ...options,
                query: { ...params },
            }),
            create: (input, options) => client.post('/warehouses', input, options),
            update: (id, input, options) => client.patch(`/warehouses/${String(id)}`, input, options),
            archive: (id, options) => client.delete(`/warehouses/${String(id)}`, undefined, options),
            restore: (id, options) => client.post(`/warehouses/${String(id)}/restore`, {}, options),
            dashboard: (id, params, options) => client.get(`/warehouses/${String(id)}/dashboard`, {
                ...options,
                query: { ...params },
            }),
            members: {
                list: (id, options) => client.get(`/warehouses/${String(id)}/members`, options),
                add: (id, input, options) => client.post(`/warehouses/${String(id)}/members`, input, options),
                setRole: (id, memberId, role, options) => client.patch(`/warehouses/${String(id)}/members/${String(memberId)}`, { role }, options),
                remove: (id, memberId, options) => client.delete(`/warehouses/${String(id)}/members/${String(memberId)}`, undefined, options),
            },
        },
        warehouse: {
            receipts: {
                list: (params, options) => client.getPaginated('/warehouse/receipts', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/warehouse/receipts/${String(id)}`, options),
                start: (input, options) => client.post('/warehouse/receipts', input, options),
                addLine: (id, input, options) => client.post(`/warehouse/receipts/${String(id)}/items`, input, options),
                updateLine: (id, lineId, input, options) => client.patch(`/warehouse/receipts/${String(id)}/items/${String(lineId)}`, input, options),
                removeLine: (id, lineId, options) => client.delete(`/warehouse/receipts/${String(id)}/items/${String(lineId)}`, undefined, options),
                complete: (id, options) => client.post(`/warehouse/receipts/${String(id)}/complete`, {}, options),
                cancel: (id, reason, options) => client.post(`/warehouse/receipts/${String(id)}/cancel`, { reason }, options),
            },
            resolveBarcode: (barcode, params, options) => client.get(`/warehouse/barcodes/${encodeURIComponent(barcode)}`, {
                ...options,
                query: { ...params },
            }),
            attachBarcode: (input, options) => client.post('/warehouse/barcodes', input, options),
            catalog: (params, options) => client.getPaginated('/warehouse/catalog', {
                ...options,
                query: { ...params },
            }),
            createProduct: (input, options) => client.post('/warehouse/catalog', input, options),
        },
        myStore: {
            list: (options) => client.get('/stores/owned', options),
            lifecycle: {
                pause: (id, options) => client.post(`/stores/${String(id)}/pause`, {}, options),
                resume: (id, options) => client.post(`/stores/${String(id)}/resume`, {}, options),
                archiveImpact: (id, options) => client.get(`/stores/${String(id)}/archive-impact`, options),
                // Addressed by id, never "mine": an account may own several, and a
                // destructive call that resolved "the caller's" would archive the
                // oldest one whatever the owner meant.
                archive: (id, reason, options) => client.delete(`/stores/${String(id)}`, reason === undefined ? undefined : { reason }, options),
            },
            get: (options) => client.get('/stores/mine', options),
            apply: (input, options) => client.post('/stores', input, options),
            update: (input, options) => client.patch('/stores/mine', input, options),
            products: {
                list: (params, options) => client.getPaginated('/stores/mine/products', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/stores/mine/products/${String(id)}`, options),
                create: (input, options) => client.post('/stores/mine/products', input, options),
                update: (id, input, options) => client.patch(`/stores/mine/products/${String(id)}`, input, options),
                remove: (id, options) => client.delete(`/stores/mine/products/${String(id)}`, undefined, options),
            },
        },
        pushDevice: {
            register: (input, options) => client.put('/auth/me/push-device', input, options),
            unregister: async (options) => {
                await client.delete('/auth/me/push-device', undefined, options);
            },
        },
        notifications: {
            list: (params, options) => client.getPaginated('/notifications', {
                ...options,
                query: { ...params },
            }),
            unreadCount: (options) => client.get('/notifications/unread-count', options),
            markRead: (id, options) => client.post(`/notifications/${String(id)}/read`, {}, options),
            markAllRead: async (options) => {
                await client.post('/notifications/read-all', {}, options);
            },
        },
        wallet: {
            balance: (options) => client.get('/wallet/balance', options),
            transactions: (params, options) => client.getPaginated('/wallet/transactions', {
                ...options,
                query: { ...params },
            }),
            receiving: (options) => client.get('/wallet/receiving', options),
            resolve: (input, options) => client.post('/wallet/resolve', input, options),
            transfers: {
                create: (input, options) => client.post('/wallet/transfers', input, options),
                list: (params, options) => client.getPaginated('/wallet/transfers', {
                    ...options,
                    query: { ...params },
                }),
                get: (id, options) => client.get(`/wallet/transfers/${String(id)}`, options),
            },
        },
        rewards: {
            list: (params, options) => client.getPaginated('/rewards', { ...options, query: { ...params } }),
        },
        rewardCodes: {
            redeem: (input, options) => client.post('/reward-codes/redeem', input, options),
        },
        maintenance: {
            list: (vehicleId, options) => client.get(`/vehicles/${String(vehicleId)}/maintenance`, options),
            update: (vehicleId, type, input, options) => client.patch(`/vehicles/${String(vehicleId)}/maintenance/${type}`, input, options),
        },
        health: (options) => client.get('/health', options),
        inventory: {
            list: (params, options) => client.getPaginated('/inventory', { ...options, query: { ...params } }),
            get: (id, options) => client.get(`/inventory/${String(id)}`, options),
            create: (input, options) => client.post('/inventory', input, options),
            movements: (id, params, options) => client.getPaginated(`/inventory/${String(id)}/movements`, {
                ...options,
                query: { ...params },
            }),
            record: (id, input, options) => client.post(`/inventory/${String(id)}/movements`, input, options),
        },
        business: {
            overview: (type, id, params, options) => client.get(`/businesses/${type}/${String(id)}/overview`, {
                ...options,
                query: { ...params },
            }),
            inventory: (type, id, params, options) => client.getPaginated(`/businesses/${type}/${String(id)}/inventory`, {
                ...options,
                query: { ...params },
            }),
            suppliers: (type, id, params, options) => client.getPaginated(`/businesses/${type}/${String(id)}/suppliers`, {
                ...options,
                query: { ...params },
            }),
            createSupplier: (type, id, input, options) => client.post(`/businesses/${type}/${String(id)}/suppliers`, input, options),
            getSupplier: (type, id, supplier, options) => client.get(`/businesses/${type}/${String(id)}/suppliers/${String(supplier)}`, options),
            updateSupplier: (type, id, supplier, input, options) => client.patch(`/businesses/${type}/${String(id)}/suppliers/${String(supplier)}`, input, options),
            deleteSupplier: (type, id, supplier, options) => client.delete(`/businesses/${type}/${String(id)}/suppliers/${String(supplier)}`, undefined, options),
            invoices: (type, id, params, options) => client.getPaginated(`/businesses/${type}/${String(id)}/invoices`, {
                ...options,
                query: { ...params },
            }),
            createInvoice: (type, id, input, options) => client.post(`/businesses/${type}/${String(id)}/invoices`, input, options),
            getInvoice: (type, id, invoice, options) => client.get(`/businesses/${type}/${String(id)}/invoices/${String(invoice)}`, options),
            receive: (type, id, invoice, input, options) => client.post(`/businesses/${type}/${String(id)}/invoices/${String(invoice)}/receipts`, input, options),
            receipts: (type, id, invoice, params, options) => client.getPaginated(`/businesses/${type}/${String(id)}/invoices/${String(invoice)}/receipts`, { ...options, query: { ...params } }),
            receipt: (type, id, invoice, receipt, options) => client.get(`/businesses/${type}/${String(id)}/invoices/${String(invoice)}/receipts/${String(receipt)}`, options),
            payables: (type, id, params, options) => client.getPaginated(`/businesses/${type}/${String(id)}/payables`, {
                ...options,
                query: { ...params },
            }),
            payable: (type, id, invoice, options) => client.get(`/businesses/${type}/${String(id)}/payables/${String(invoice)}`, options),
            payments: (type, id, params, options) => client.getPaginated(`/businesses/${type}/${String(id)}/payments`, {
                ...options,
                query: { ...params },
            }),
            pay: (type, id, input, options) => client.post(`/businesses/${type}/${String(id)}/payments`, input, options),
            statement: (type, id, supplier, options) => client.get(`/businesses/${type}/${String(id)}/suppliers/${String(supplier)}/statement`, options),
            expenses: (type, id, params, options) => client.getPaginated(`/businesses/${type}/${String(id)}/expenses`, {
                ...options,
                query: { ...params },
            }),
            sales: (type, id, params, options) => client.getPaginated(`/businesses/${type}/${String(id)}/sales`, {
                ...options,
                query: { ...params },
            }),
            createSale: (type, id, input, options) => client.post(`/businesses/${type}/${String(id)}/sales`, input, options),
            price: (type, id, item, input, options) => client.patch(`/businesses/${type}/${String(id)}/inventory/${String(item)}/pricing`, input, options),
            threshold: (type, id, item, input, options) => client.patch(`/businesses/${type}/${String(id)}/inventory/${String(item)}/threshold`, input, options),
            count: (type, id, item, input, options) => client.post(`/businesses/${type}/${String(id)}/inventory/${String(item)}/counts`, input, options),
            inventoryAlerts: (type, id, params, options) => client.getPaginated(`/businesses/${type}/${String(id)}/inventory-alerts`, {
                ...options,
                query: { ...params },
            }),
            pricing: (type, id, item, params, options) => client.get(`/businesses/${type}/${String(id)}/inventory/${String(item)}/pricing`, { ...options, query: { ...params } }),
            exit: (type, id, item, input, options) => client.post(`/businesses/${type}/${String(id)}/inventory/${String(item)}/exits`, input, options),
            movements: (type, id, params, options) => client.getPaginated(`/businesses/${type}/${String(id)}/movements`, {
                ...options,
                query: { ...params },
            }),
            consumeMaintenance: (business, record, item, input, options) => client.post(`/businesses/workshop/${String(business)}/service-records/${String(record)}/inventory/${String(item)}`, input, options),
            createExpense: (type, id, input, options) => client.post(`/businesses/${type}/${String(id)}/expenses`, input, options),
        },
    };
}
/** Builds the five review actions for one listing kind. */
function listingSection(client, base) {
    return {
        list: (params, options) => client.getPaginated(base, { ...options, query: { ...params } }),
        get: (id, options) => client.get(`${base}/${String(id)}`, options),
        approve: (id, options) => client.post(`${base}/${String(id)}/approve`, {}, options),
        reject: (id, input, options) => client.post(`${base}/${String(id)}/reject`, input, options),
        suspend: (id, input, options) => client.post(`${base}/${String(id)}/suspend`, input, options),
    };
}
//# sourceMappingURL=endpoints.js.map